import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { FleetMockStore } from '../mock/fleet-mock.store';
import { FleetKpis, FleetVehicle, FLEET_STATUS_META } from '../mock/models/fleet.models';

type Period = 'today' | 'week' | 'month';

/**
 * FleetAnalyticsDashboardComponent — dashboard analítico a NIVEL DE FLOTA.
 * Lee `store.kpis$` (KPIs agregados) y pinta tarjetas + gráficos ApexCharts.
 * Los colores del semáforo salen de FLEET_STATUS_META para no desincronizar
 * con el mapa / tabla.
 */
@Component({
  selector: 'app-fleet-analytics-dashboard',
  templateUrl: './fleet-analytics-dashboard.component.html',
  styleUrls: ['./fleet-analytics-dashboard.component.css'],
})
export class FleetAnalyticsDashboardComponent implements OnInit, OnDestroy {
  kpis?: FleetKpis;

  // Selector para el segundo nivel (analíticas por-vehículo).
  vehicles: FleetVehicle[] = [];
  selectedVehicleId: string | null = null;

  // Meta del semáforo expuesta al template (colores/labels reutilizables).
  readonly statusMeta = FLEET_STATUS_META;

  // Colores EXACTOS del semáforo: Moving / Stopped / Idle.
  private readonly statusColors = [FLEET_STATUS_META.MOVING.hex, FLEET_STATUS_META.STOPPED.hex, FLEET_STATUS_META.IDLE.hex];
  private readonly monthLabels = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

  // Filtro de periodo (con etiqueta de fecha VÁLIDA — evita el bug 12/32).
  period: Period = 'month';
  dateRangeLabel = '';

  // ---- Opciones ApexCharts (tipado laxo: el proyecto permite any). ----
  donut: any;
  fuelTrend: any;
  ranking: any;

  private readonly subs = new Subscription();

  constructor(private store: FleetMockStore) {
    // do nothing
  }

  ngOnInit(): void {
    this.setPeriod(this.period);
    this.subs.add(
      this.store.kpis$.subscribe((k) => {
        this.kpis = k;
        this.buildCharts(k);
      }),
    );
    this.subs.add(
      this.store.fleet$.subscribe((fleet) => {
        this.vehicles = fleet;
        if (!this.selectedVehicleId && fleet.length) {
          this.selectedVehicleId = fleet[0].id ?? null;
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  selectVehicle(id: string | null): void {
    this.selectedVehicleId = id;
  }

  setPeriod(p: Period): void {
    this.period = p;
    const end = new Date(2026, 6, 10); // 10 jul 2026 (fecha válida, sin 12/32)
    const start = new Date(end);
    if (p === 'today') {
      // mismo día
    } else if (p === 'week') {
      start.setDate(end.getDate() - 6);
    } else {
      start.setDate(end.getDate() - 29);
    }
    this.dateRangeLabel = `${this.fmt(start)} - ${this.fmt(end)}`;
  }

  private fmt(d: Date): string {
    const dd = `${d.getDate()}`.padStart(2, '0');
    const mm = `${d.getMonth() + 1}`.padStart(2, '0');
    return `${dd}/${mm}/${d.getFullYear()}`;
  }

  /** Formatea COP compacto para tooltips/labels. */
  cop(value: number): string {
    return `$${(value * 1000).toLocaleString('es-CO')}`;
  }

  private buildCharts(k: FleetKpis): void {
    // DONUT — desglose de estado de la flota.
    this.donut = {
      series: [k.moving, k.stopped, k.idle],
      chart: { type: 'donut', height: 300, fontFamily: 'inherit', foreColor: '#888ea8' },
      labels: [this.statusMeta.MOVING.label, this.statusMeta.STOPPED.label, this.statusMeta.IDLE.label],
      colors: this.statusColors,
      stroke: { width: 2, colors: ['transparent'] },
      legend: { position: 'bottom', labels: { colors: '#888ea8' } },
      dataLabels: { enabled: true },
      plotOptions: {
        pie: {
          donut: {
            size: '62%',
            labels: {
              show: true,
              total: { show: true, label: 'Total', color: '#888ea8', formatter: () => `${k.total}` },
            },
          },
        },
      },
      tooltip: { theme: 'dark' },
      responsive: [{ breakpoint: 480, options: { chart: { height: 260 } } }],
    };

    // ÁREA — tendencia mensual del costo de combustible de la flota.
    this.fuelTrend = {
      series: [{ name: 'Costo combustible (COP)', data: k.fuelCostSeries.map((v) => Math.round(v * 1000)) }],
      chart: { type: 'area', height: 320, fontFamily: 'inherit', foreColor: '#888ea8', toolbar: { show: false }, zoom: { enabled: false } },
      colors: ['#4361ee'],
      dataLabels: { enabled: false },
      stroke: { curve: 'smooth', width: 2 },
      xaxis: { categories: this.monthLabels, axisBorder: { show: false }, axisTicks: { show: false } },
      yaxis: { labels: { formatter: (val: number) => `$${(val / 1000000).toFixed(1)}M` } },
      grid: { borderColor: 'rgba(136,142,168,0.15)' },
      fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.05, stops: [0, 100] } },
      tooltip: { theme: 'dark', y: { formatter: (val: number) => `$${val.toLocaleString('es-CO')}` } },
    };

    // BARRAS — Top 3 costo por Km.
    this.ranking = {
      series: [{ name: 'Costo por Km (COP)', data: k.costliestPerKm.map((c) => c.costPerKm) }],
      chart: { type: 'bar', height: 240, fontFamily: 'inherit', foreColor: '#888ea8', toolbar: { show: false } },
      colors: ['#e7515a'],
      plotOptions: { bar: { horizontal: true, borderRadius: 4, distributed: false, barHeight: '55%' } },
      dataLabels: { enabled: true, formatter: (val: number) => `$${val.toLocaleString('es-CO')}`, style: { colors: ['#fff'] } },
      xaxis: { categories: k.costliestPerKm.map((c) => c.plate), labels: { formatter: (val: number) => `$${val}` } },
      grid: { borderColor: 'rgba(136,142,168,0.15)' },
      tooltip: { theme: 'dark', y: { formatter: (val: number) => `$${val.toLocaleString('es-CO')} / km` } },
    };
  }
}
